// Sitzung der Oberfläche (zustand): wer ist angemeldet, in welchem Betrieb, an welchem Standort, mit welchen
// Rechten. Quelle ist allein der Server (GET /api/session, docs/API.md §3.1) – hier wird nichts abgeleitet,
// was der Server nicht gesagt hat. Rechte in der Oberfläche blenden nur aus; geprüft wird auf dem Server.
//
// Einzige Ausnahme ist der Start ohne Server (docs/kasse-offline/SPEZIFIKATION.md §14.7, Zustand 'offline-till'): Dann
// gilt eine Ersatz-Sitzung aus dem Kontext einer Probe (web/shell/offlineStart.ts) – eine Person, ein Betrieb, ein
// Standort, genau die genannten Rechte, keine Aktion. Sie endet mit der ersten echten Sitzung (resumeSession).
import { create } from 'zustand'
import type { LoginMfaBody } from '@shared/auth'
import { toAppError, type AppError } from '@shared/errors'
import { getApiClient, isNetworkError } from '../api/client'
import type { LoginInput, SessionPayload } from '../api/types'
import type { OfflineStartContext } from '../module'
import { resetSecurityNotices } from './sessionNotices'

/**
 * Zeitlimit der Sitzungsabfrage (docs/kasse-offline/SPEZIFIKATION.md §14.7, §17; derselbe Wert wie
 * `SESSION_TIMEOUT_MS` in modules/pos/lib/offline/limits.ts – die Hülle kennt kein Modul).
 */
export const SESSION_TIMEOUT_MS = 5_000

export type SessionStatus =
  /** noch nicht gefragt */
  | 'unknown'
  | 'loading'
  | 'authenticated'
  /** nicht angemeldet → Anmeldeseite */
  | 'anonymous'
  /** der Server war nicht erreichbar – erneut versuchen */
  | 'error'
  /** Start ohne Server: nur die Route der Probe, mit Ersatz-Sitzung (§14.7) */
  | 'offline-till'

/** Ergebnis einer Probe für den Start ohne Server (web/shell/offlineStart.ts) */
export interface OfflineStart {
  /** Modul, dessen Probe geantwortet hat ('' = Test-Probe des E2E-Aufbaus) */
  module: string
  /** die eine Route, die ohne Server läuft */
  path: string
  context: OfflineStartContext
}

export interface SessionState {
  status: SessionStatus
  session: SessionPayload | null
  error: AppError | null
  /** warum die letzte Sitzung endete – die Anmeldeseite sagt es dazu */
  ended: 'expired' | 'logout' | null
  /** nur im Zustand 'offline-till': welche Route ohne Server läuft; `entered` = die Route wurde einmal erreicht */
  offline: (OfflineStart & { entered: boolean }) | null
}

export const useSessionStore = create<SessionState>(() => ({
  status: 'unknown',
  session: null,
  error: null,
  ended: null,
  offline: null,
}))

const authenticated = (session: SessionPayload): void =>
  useSessionStore.setState({ status: 'authenticated', session, error: null, ended: null, offline: null })

const anonymous = (ended: SessionState['ended'] = null): void =>
  useSessionStore.setState({ status: 'anonymous', session: null, error: null, ended, offline: null })

/**
 * Ersatz-Sitzung aus dem Kontext einer Probe: ein Standort, genau dessen Rechte, keine Aktion des Servers, keine
 * Mitgliedschaften (kein Betriebswechsel). Hausfarbe und Logo bleiben, wie sie zuletzt gemerkt waren (web/lib/theme).
 */
export function substituteSession(context: OfflineStartContext, now: Date = new Date()): SessionPayload {
  const at = now.toISOString()
  return {
    user: { id: context.user.id, email: '', displayName: context.user.displayName, locale: 'de' },
    tenant: {
      id: context.tenant.id,
      name: context.tenant.name,
      slug: '',
      accentColor: null,
      accentStyle: null,
      logoUrl: null,
      features: [...(context.features ?? [])],
      demo: context.tenant.demo,
      demoNotice: null,
    },
    memberships: [],
    locations: [{ id: context.location.id, code: context.location.code, name: context.location.name, kind: 'store' }],
    activeLocationId: context.location.id,
    permissions: { all: [], byLocation: { [context.location.id]: [...(context.permissions ?? [])] } },
    actions: [],
    serverTime: at,
    expiresAt: at,
  }
}

export interface LoadSessionOptions {
  /**
   * Start ohne Server: wird gefragt, wenn die Sitzung OHNE Antwort von Merxo scheitert (network, timeout, foreign –
   * nie bei einer Antwort der Kante oder des Servers). Liefert sie ein Ergebnis, gilt der Zustand 'offline-till'.
   */
  offlineStart?: () => Promise<OfflineStart | null>
}

let loading: Promise<SessionPayload | null> | null = null

/**
 * Sitzung vom Server holen (Start der Anwendung, „Erneut versuchen“) – höchstens SESSION_TIMEOUT_MS. Gleichzeitige
 * Aufrufe teilen sich die Anfrage. Scheitert sie ohne Antwort von Merxo, fragt sie `options.offlineStart`.
 */
export function loadSession(options: LoadSessionOptions = {}): Promise<SessionPayload | null> {
  if (loading) return loading
  if (useSessionStore.getState().status !== 'authenticated')
    useSessionStore.setState({ status: 'loading', error: null })
  loading = getApiClient()
    .session({ silentUnauthenticated: true, timeoutMs: SESSION_TIMEOUT_MS })
    .then(
      (session) => {
        // eine leere Antwort gilt wie 401: nicht angemeldet
        if (!session) {
          anonymous()
          return null
        }
        authenticated(session)
        return session
      },
      async (cause: unknown) => {
        const error = toAppError(cause)
        if (error.code === 'UNAUTHENTICATED') {
          anonymous()
          return null
        }
        // Nur ohne Antwort von Merxo: Eine Störung des Servers (edge, server) ist kein Grund, ohne ihn zu arbeiten
        const start = options.offlineStart && isNetworkError(error) ? await options.offlineStart().catch(() => null) : null
        if (start) {
          useSessionStore.setState({
            status: 'offline-till',
            session: substituteSession(start.context),
            error,
            ended: null,
            offline: { ...start, entered: false },
          })
        } else {
          useSessionStore.setState({ status: 'error', session: null, error, offline: null })
        }
        return null
      },
    )
    .finally(() => {
      loading = null
    })
  return loading
}

/**
 * Angemeldete Sitzung still neu holen – nach dem Umschalten einer Funktion folgen Navigation, Seiten und Befehle sofort,
 * ohne Neuladen (docs/ohne-lager/SPEZIFIKATION.md). Ohne Ladebildschirm; scheitert die Anfrage, bleibt die bisherige
 * Sitzung, bis die Seite neu lädt.
 */
export async function refreshSession(): Promise<void> {
  if (useSessionStore.getState().status !== 'authenticated') return
  try {
    const session = await getApiClient().session({ silentUnauthenticated: true, timeoutMs: SESSION_TIMEOUT_MS })
    if (session && useSessionStore.getState().status === 'authenticated') authenticated(session)
  } catch {
    // nur eine Bequemlichkeit: Die Rechte prüft ohnehin der Server
  }
}

/** höchstens so oft holt ein Tab beim Zurückkommen die Sitzung neu (Funktionen, Rechte, Standorte – in einem anderen Tab oder Gerät geändert) */
export const SESSION_REFRESH_INTERVAL_MS = 2 * 60_000
let watchingFreshness = false
let lastFreshness = 0

/**
 * Beim Wechsel in den Vordergrund die Sitzung still neu holen, höchstens alle SESSION_REFRESH_INTERVAL_MS: Schaltet jemand
 * in einem anderen Tab oder auf einem anderen Gerät eine Funktion ein, folgt die Navigation ohne Neuladen.
 */
export function watchSessionFreshness(now: () => number = Date.now): void {
  if (watchingFreshness || typeof document === 'undefined') return
  watchingFreshness = true
  lastFreshness = now()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || now() - lastFreshness < SESSION_REFRESH_INTERVAL_MS) return
    lastFreshness = now()
    void refreshSession()
  })
}

/** Nur für Tests. */
export function resetSessionFreshness(): void {
  watchingFreshness = false
  lastFreshness = 0
}

let resuming: Promise<boolean> | null = null

/**
 * Nur im Zustand 'offline-till': die echte Sitzung im Hintergrund nachladen (ohne Ladebildschirm, ohne Neuladen).
 * true = der Zustand hat gewechselt (angemeldet bzw. abgelaufen → Anmeldung); false = weiter ohne Server (keine Antwort
 * oder eine Störung des Servers – die Ersatz-Sitzung bleibt).
 */
export function resumeSession(): Promise<boolean> {
  if (useSessionStore.getState().status !== 'offline-till') return Promise.resolve(false)
  if (resuming) return resuming
  const stillOffline = (): boolean => useSessionStore.getState().status === 'offline-till'
  resuming = getApiClient()
    .session({ silentUnauthenticated: true, timeoutMs: SESSION_TIMEOUT_MS })
    .then(
      (session) => {
        if (!stillOffline()) return false
        if (session) authenticated(session)
        else anonymous('expired')
        return true
      },
      (cause: unknown) => {
        if (!stillOffline() || toAppError(cause).code !== 'UNAUTHENTICATED') return false
        anonymous('expired')
        return true
      },
    )
    .finally(() => {
      resuming = null
    })
  return resuming
}

/** Die Route des Starts ohne Server ist erreicht: ab jetzt zeigt jede andere Route die Fehlerseite statt umzuleiten. */
export function markOfflineEntered(): void {
  const { status, offline } = useSessionStore.getState()
  if (status === 'offline-till' && offline && !offline.entered) useSessionStore.setState({ offline: { ...offline, entered: true } })
}

/**
 * Anmelden. Fehler (falsches Kennwort, Drosselung) kommen als AppError mit deutschem Satz zurück. Verlangt der Zugang
 * einen zweiten Faktor, ist es ein UNAUTHENTICATED mit `details.reason` 'mfa_required' bzw. 'mfa_setup_required'
 * (zLoginChallenge) – die Anmeldeseite fragt dann den Code ab (loginMfa) bzw. führt durch die Einrichtung.
 */
export async function login(input: LoginInput): Promise<SessionPayload> {
  const session = await getApiClient().login(input)
  establishSession(session)
  return session
}

/** Zweiter Schritt der Anmeldung: Code aus der App oder Wiederherstellungscode. */
export async function loginMfa(input: LoginMfaBody): Promise<SessionPayload> {
  const session = await getApiClient().loginMfa(input)
  establishSession(session)
  return session
}

/**
 * Eine neue Sitzung übernehmen (Anmeldung, Code, Einrichtung bei der Anmeldung). Hinweise der Sicherheitsleiste aus
 * einer früheren Anmeldung gelten dann wieder.
 */
export function establishSession(session: SessionPayload): void {
  resetSecurityNotices()
  authenticated(session)
}

// ---- Abmelde-Wächter (docs/kasse-offline/SPEZIFIKATION.md §12.2, §14.7) ----
// Ein Modul meldet einen Wächter an (Kasse: solange ein Bon läuft oder Bons ohne Verbindung warten). Liefert er einen
// Satz, meldet die Hülle nicht ab, sondern zeigt ihn.

const logoutGuards = new Set<() => string | null>()

/** Wächter anmelden; liefert die Funktion zum Abmelden des Wächters. */
export function registerLogoutGuard(guard: () => string | null): () => void {
  logoutGuards.add(guard)
  return () => {
    logoutGuards.delete(guard)
  }
}

/** Satz des ersten Wächters, der das Abmelden gerade sperrt – null: Abmelden ist möglich. */
export function logoutBlockedReason(): string | null {
  for (const guard of logoutGuards) {
    try {
      const reason = guard()
      if (reason) return reason
    } catch (error) {
      // ein kaputter Wächter sperrt nicht für immer – Abmelden löscht keine Daten des Geräts
      if (import.meta.env.DEV) console.error('[Abmelden] Ein Wächter ist gescheitert.', error)
    }
  }
  return null
}

/** Abmelden. false = ein Wächter sperrt (nichts geschehen; den Satz liefert logoutBlockedReason()). */
export async function logout(options: { everywhere?: boolean } = {}): Promise<boolean> {
  if (logoutBlockedReason() !== null) return false
  try {
    await getApiClient().logout(options)
  } finally {
    // auch wenn der Server nicht antwortet: Die Oberfläche zeigt nichts mehr aus der Sitzung
    anonymous('logout')
  }
  return true
}

/** Betrieb wechseln (Nutzer mit mehreren Mitgliedschaften). */
export async function selectTenant(tenantId: string): Promise<SessionPayload> {
  const session = await getApiClient().selectTenant(tenantId)
  authenticated(session)
  return session
}

/** Arbeitsstandort wechseln; null = „Alle Standorte“. */
export async function setActiveLocation(locationId: string | null): Promise<SessionPayload> {
  const session = await getApiClient().setLocation(locationId)
  authenticated(session)
  return session
}

/** Der Server hat eine Anfrage mit 401 beantwortet: Die Sitzung ist abgelaufen oder wurde beendet. */
export function sessionExpired(): void {
  if (useSessionStore.getState().status !== 'authenticated') return
  anonymous('expired')
}

/** Nur für Tests und den Schaukasten: Zustand direkt setzen. */
export function setSessionForTesting(session: SessionPayload | null): void {
  useSessionStore.setState(
    session
      ? { status: 'authenticated', session, error: null, ended: null, offline: null }
      : { status: 'anonymous', session: null, error: null, ended: null, offline: null },
  )
}

/** Kennzeichen, an dem sich erkennt, ob Nutzer oder Betrieb gewechselt haben (dann gehört der Abfrage-Cache geleert). */
export const sessionIdentity = (session: SessionPayload | null): string =>
  session ? `${session.user.id}:${session.tenant?.id ?? ''}` : ''
